import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Chip, Stack, Alert, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, IconButton, Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { parseJsonArray } from '../../Utils/frameworkTiers';
import {
  ListEntitlementCodes, ListCodeRedemptions, UpdateEntitlementCode, DeleteEntitlementCode,
} from '../../Api/Billing';
import StandardListView from '../StandardComponents/StandardListView/StandardListView';
import EntitlementCodeDialog from './EntitlementCodeDialog';
import ConfirmDialog from './ConfirmDialog';
import UnlockCodesQuickFilters from './UnlockCodesQuickFilters';
import '../../Components/Common/GlobalStyles.css';

const unwrap = (res) => res?.data?.data ?? res?.data ?? [];
const formatTiers = (tiers) => parseJsonArray(tiers).join(', ') || String(tiers || '');
const truncate = (s, n = 18) => (s ? (s.length > n ? `${s.slice(0, n)}…` : s) : '—');

const matchesSearch = (c, q) => {
  const code = (c.code || '').toLowerCase();
  const tiers = formatTiers(c.tiers).toLowerCase();
  const active = c.is_active ? 'yes' : 'no';
  return code.includes(q) || tiers.includes(q) || active.includes(q);
};

const matchesQuickFilter = (c, activeFilters) => {
  if (activeFilters.is_active !== undefined && c.is_active !== activeFilters.is_active) return false;
  if (activeFilters.unused === true && (c.used_count ?? 0) !== 0) return false;
  if (activeFilters.has_uses === true && (c.used_count ?? 0) === 0) return false;
  return true;
};

/**
 * UnlockCodesPanel — global unlock-code management with StandardListView, quick
 * filters, search, redemption drill-down, and create/edit dialogs.
 */
export default function UnlockCodesPanel({ infoExtra }) {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState({});
  const [codeDialog, setCodeDialog] = useState({ open: false, mode: 'create', code: null });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expandedCode, setExpandedCode] = useState(null);
  const [redemptions, setRedemptions] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ListEntitlementCodes();
      setCodes(unwrap(res));
    } catch (e) {
      toast.error('Failed to load unlock codes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visibleCodes = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return codes.filter((c) => {
      if (!matchesQuickFilter(c, activeFilters)) return false;
      if (q && !matchesSearch(c, q)) return false;
      return true;
    });
  }, [codes, activeFilters, searchQuery]);

  const expandedRow = useMemo(
    () => codes.find((c) => c.id === expandedCode) || null,
    [codes, expandedCode]
  );

  useEffect(() => {
    if (!expandedCode || redemptions[expandedCode]) return;
    setRedemptions((m) => ({ ...m, [expandedCode]: { loading: true, rows: [] } }));
    ListCodeRedemptions(expandedCode)
      .then((res) => {
        setRedemptions((m) => ({ ...m, [expandedCode]: { loading: false, rows: unwrap(res) } }));
      })
      .catch(() => {
        setRedemptions((m) => ({ ...m, [expandedCode]: { loading: false, rows: [] } }));
        toast.error('Failed to load redemptions');
      });
  }, [expandedCode, redemptions]);

  const handleApplyFilter = useCallback((filter) => {
    setActiveFilters((prev) => {
      if (prev[filter.field] === filter.value) {
        const next = { ...prev };
        delete next[filter.field];
        return next;
      }
      return { ...prev, [filter.field]: filter.value };
    });
  }, []);

  const toggleActive = async (c) => {
    try {
      await UpdateEntitlementCode(c.id, {
        code_kind: c.code_kind || 'grant',
        stripe_coupon_id: c.stripe_coupon_id ?? null,
        discount_percent: c.discount_percent ?? null,
        discount_applies_to: c.discount_applies_to ?? null,
        tiers: parseJsonArray(c.tiers),
        is_active: !c.is_active,
        max_uses: c.max_uses ?? null,
        grant_duration_days: c.grant_duration_days ?? null,
        valid_until: c.valid_until || null,
        notes: c.notes ?? null,
      });
      toast.success(c.is_active ? 'Code deactivated' : 'Code activated');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Update failed');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await DeleteEntitlementCode(deleteTarget.id);
      toast.success('Code deleted');
      if (expandedCode === deleteTarget.id) setExpandedCode(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Delete failed');
    } finally {
      setDeleteTarget(null);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code).then(
      () => toast.success('Copied'),
      () => toast.error('Copy failed')
    );
  };

  const columns = useMemo(() => [
    {
      label: 'Code',
      field: 'code',
      minWidth: 140,
      render: (row) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500, color: '#374151' }}>
            {row.code}
          </Typography>
          <Tooltip title="Copy Code">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); copyCode(row.code); }}>
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
    {
      label: 'Kind',
      field: 'code_kind',
      minWidth: 140,
      render: (row) => {
        const isDiscount = row.code_kind === 'stripe_discount';
        const scope = row.discount_applies_to === 'essentials'
          ? 'Essentials'
          : row.discount_applies_to === 'upgrades'
            ? 'Upgrades'
            : row.discount_applies_to === 'all'
              ? 'All plans'
              : null;
        return (
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              variant="outlined"
              color={isDiscount ? 'warning' : 'default'}
              label={isDiscount ? 'Checkout %' : 'Full unlock'}
            />
            {isDiscount && scope ? (
              <Chip size="small" variant="outlined" label={scope} />
            ) : null}
          </Stack>
        );
      },
    },
    {
      label: 'Tiers',
      field: 'tiers',
      minWidth: 160,
      render: (row) => (
        <Typography variant="body2" color="text.secondary">
          {row.code_kind === 'stripe_discount'
            ? (row.discount_percent ? `${row.discount_percent}% → ${row.stripe_coupon_id || '—'}` : (row.stripe_coupon_id || '—'))
            : formatTiers(row.tiers)}
        </Typography>
      ),
    },
    {
      label: 'Uses',
      field: 'used_count',
      minWidth: 80,
      render: (row) => (
        <Typography variant="body2" color="text.secondary">
          {row.used_count ?? 0}{row.max_uses ? ` / ${row.max_uses}` : ''}
        </Typography>
      ),
    },
    {
      label: 'Grant Days',
      field: 'grant_duration_days',
      minWidth: 90,
      render: (row) => (
        <Typography variant="body2" color="text.secondary">{row.grant_duration_days ?? '—'}</Typography>
      ),
    },
    {
      label: 'Valid Until',
      field: 'valid_until',
      minWidth: 110,
      render: (row) => (
        <Typography variant="body2" color="text.secondary">
          {row.valid_until ? new Date(row.valid_until).toLocaleDateString() : '—'}
        </Typography>
      ),
    },
    {
      label: 'Active',
      field: 'is_active',
      minWidth: 80,
      render: (row) => (
        <Chip
          size="small"
          label={row.is_active ? 'Yes' : 'No'}
          color={row.is_active ? 'success' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      label: 'Notes',
      field: 'notes',
      minWidth: 120,
      render: (row) => (
        row.notes ? (
          <Tooltip title={row.notes}>
            <Typography variant="body2" color="text.secondary">{truncate(row.notes)}</Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2" color="text.secondary">—</Typography>
        )
      ),
    },
  ], []);

  const actions = useMemo(() => [
    {
      label: 'View Redemptions',
      onClick: (row) => setExpandedCode((prev) => (prev === row.id ? null : row.id)),
    },
    {
      label: 'Edit',
      onClick: (row) => setCodeDialog({ open: true, mode: 'edit', code: row }),
    },
    {
      label: 'Deactivate',
      onClick: toggleActive,
      condition: (row) => row.is_active,
    },
    {
      label: 'Activate',
      onClick: toggleActive,
      condition: (row) => !row.is_active,
    },
    {
      label: 'Delete',
      onClick: (row) => setDeleteTarget(row),
      condition: (row) => (row.used_count ?? 0) === 0,
    },
  ], []);

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#fff', overflow: 'hidden' }}>
      <Box sx={{ px: 2, pt: 2 }}>
        <Alert severity="info" sx={{ mb: 0 }}>
          Unlock codes are stored in Zavior. Full-unlock codes grant tiers directly.
          Checkout-discount codes link to a Stripe coupon (use Sync in the edit form to verify) —
          they are not a two-way catalog sync.
          A code is not tied to an organization: any org&apos;s admin can redeem it for their own org (source <strong>code</strong>).
          {infoExtra}
        </Alert>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <StandardListView
            title="Unlock Codes"
            columns={columns}

            rows={visibleCodes}
            actions={actions}
            isEdit
            showMoreIcon
            fullHeight={false}
            fillAvailableHeight={false}
            bottomPaddingPx={8}
            tableHeightOffset={120}
            addButtonLabel="Create Code"
            onAddButtonClick={() => setCodeDialog({ open: true, mode: 'create', code: null })}
            onRowClick={(row) => setCodeDialog({ open: true, mode: 'edit', code: row })}
            quickFilterComponent={(
              <UnlockCodesQuickFilters
                codes={codes}
                activeFilters={activeFilters}
                onApplyFilter={handleApplyFilter}
              />
            )}
            enableQuickFilter
            searchQuery={searchQuery}
            onSearch={setSearchQuery}
            onClearSearch={() => setSearchQuery('')}
            disableInternalSearch
          />

          {expandedRow && (
            <Box sx={{ px: 2, py: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#FAFAFA' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Redemptions — <span style={{ fontFamily: 'monospace' }}>{expandedRow.code}</span>
                </Typography>
                <IconButton size="small" onClick={() => setExpandedCode(null)} aria-label="Close redemptions">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
              {redemptions[expandedRow.id]?.loading ? (
                <CircularProgress size={20} />
              ) : (redemptions[expandedRow.id]?.rows || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">No redemptions yet.</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Organization</strong></TableCell>
                      <TableCell><strong>Redeemed At</strong></TableCell>
                      <TableCell><strong>Redeemed By</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {redemptions[expandedRow.id].rows.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell>{r.organization_name || r.organization_id}</TableCell>
                        <TableCell>{r.redeemed_at ? new Date(r.redeemed_at).toLocaleString() : '—'}</TableCell>
                        <TableCell>{r.redeemed_by_email || r.redeemed_by || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          )}
        </>
      )}

      <EntitlementCodeDialog
        open={codeDialog.open}
        mode={codeDialog.mode}
        code={codeDialog.code}
        onClose={() => setCodeDialog((d) => ({ ...d, open: false }))}
        onSaved={load}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Unlock Code"
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        confirmLabel="Delete"
      >
        Permanently delete code <strong>{deleteTarget?.code}</strong>? Deactivate instead if you want to keep its history.
      </ConfirmDialog>
    </Box>
  );
}
